import { LitElement, html, unsafeCSS, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { InternalSupportItem } from "../../types/SupportItem.js";
import questionMarkCircleRaw from "@interstate/icons-svg/question-mark-circle.svg?raw";
import phoneRaw from "@interstate/icons-svg/phone.svg?raw";
import clipboardRaw from "@interstate/icons-svg/clipboard.svg?raw";
import informationCircleRaw from "@interstate/icons-svg/information-circle.svg?raw";
import tailwindShared from "../../../styles/tailwind-shared.css?inline";
import styles from "./SupportMenuItem.css?inline";

// ARGUS PR #164 (MINOR): hoisted so the ~40-token class string isn't duplicated
// across the link / onClick-button / default-button render branches below.
const ITEM_CARD_CLASSES =
  "item-card group flex items-center gap-4 p-4 w-full box-border cursor-pointer text-left no-underline text-inherit border border-gray-100 rounded-lg bg-white transition-colors duration-150 hover:border-primary-600 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700";

// iconKey fallback resolves to an inline @interstate/icons-svg glyph (not <ids-icon>).
// The glyphs carry a viewBox but no size — inject 16×16 to match the baked default SVGs.
const withIconSize = (svg: string): string =>
  svg.replace(/<svg\b/, '<svg width="16" height="16"');

// help/phone/clipboard are the first-party keys; anything unmapped → a neutral glyph.
const ICON_SVG_BY_KEY: Readonly<Record<string, string>> = {
  help: withIconSize(questionMarkCircleRaw),
  phone: withIconSize(phoneRaw),
  clipboard: withIconSize(clipboardRaw),
};
const DEFAULT_ICON_SVG = withIconSize(informationCircleRaw);

// Absent iconKey → no icon; unmapped → default glyph; mapped → that glyph.
const resolveIconSvg = (iconKey?: string): string | undefined =>
  iconKey ? (ICON_SVG_BY_KEY[iconKey] ?? DEFAULT_ICON_SVG) : undefined;

export class SupportMenuItem extends LitElement {
  static readonly styles = [unsafeCSS(tailwindShared), unsafeCSS(styles)];

  @property({ type: Object }) item!: InternalSupportItem;
  @state() private _iconImgError = false;

  override updated(changed: Map<PropertyKey, unknown>): void {
    if (changed.has("item")) {
      // Only reset the error flag when the image URL itself changes — avoids a
      // spurious extra render cycle when the same item reference is re-set.
      const prev = changed.get("item") as InternalSupportItem | undefined;
      if (prev?.iconImg !== this.item?.iconImg) {
        this._iconImgError = false;
      }
    }
  }

  private get _isLink(): boolean {
    return !!this.item?.href;
  }

  // Allowlist safe URL schemes — blocks javascript:, data:, vbscript:, etc.
  // Relative paths that throw URL constructor fall through to "#" (safe).
  private get _href(): string {
    const raw = this.item?.href;
    if (!raw) return "#";
    try {
      const u = new URL(raw);
      return ["http:", "https:", "mailto:"].includes(u.protocol) ? raw : "#";
    } catch {
      return "#";
    }
  }

  // Lightweight guard before unsafeHTML: must look like an SVG element and
  // must not contain script tags or inline event handlers.
  private _isSafeSvg(svg: string): boolean {
    const s = svg.trim();
    return (
      /^<svg[\s>]/i.test(s) && !/<script/i.test(s) && !/\bon\w+\s*=/i.test(s)
    );
  }

  // Returns only the icon body (without the .icon-container wrapper). The
  // wrapper is rendered statically in _renderCardContent so that Lit's part
  // markers stay safely anchored — happy-dom misplaces them when a method
  // expression sits at a template's top level next to sibling elements
  // (manifested as label text leaking out of .item-title).
  private _renderIconBody() {
    if (!this.item) return nothing;
    if (this.item.iconImg && !this._iconImgError) {
      return html`<img
        class="block"
        src=${this.item.iconImg}
        alt=""
        role="presentation"
        width="20"
        height="20"
        @error=${() => {
          this._iconImgError = true;
        }}
      />`;
    }
    if (this.item.iconSvg && this._isSafeSvg(this.item.iconSvg)) {
      // Return the directive result directly. Wrapping it in a sole-content
      // html`${...}` template breaks the directive in happy-dom — the inner
      // SVG is parsed as a `<?>` processing-instruction stub.
      return unsafeHTML(this.item.iconSvg);
    }
    // Inline glyph via the same _isSafeSvg/unsafeHTML path (returned directly for happy-dom).
    const fallbackSvg = resolveIconSvg(this.item.iconKey);
    if (fallbackSvg && this._isSafeSvg(fallbackSvg)) {
      return unsafeHTML(fallbackSvg);
    }
    return nothing;
  }

  private _renderCardContent() {
    if (!this.item) return nothing;
    // .icon-container is rendered unconditionally even when _renderIconBody()
    // returns `nothing` — preserves a fixed-width layout slot so items with
    // and without icons align consistently across the panel.
    return html`
      <div
        class="icon-container flex items-center justify-center shrink-0 w-10 h-10 rounded-lg bg-primary-50 text-primary-600 transition-colors duration-150 group-hover:bg-primary-600 group-hover:text-white"
      >
        ${this._renderIconBody()}
      </div>
      <div class="flex flex-col gap-0.5 flex-1">
        <span class="item-title block text-sm font-medium"
          >${this.item.label}</span
        >
        ${this.item.subtitle
          ? html`<span
              class="item-subtitle block text-xs font-medium text-gray-600"
              >${this.item.subtitle}</span
            >`
          : nothing}
      </div>
    `;
  }

  private _emitSupportSelect(): void {
    if (!this.item) return;
    this.dispatchEvent(
      new CustomEvent("support-select", {
        detail: { option: this.item.label, id: this.item.id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (!this.item) return nothing;

    const onClick = this.item.onClick;

    if (this._isLink) {
      return html`
        <a
          class="${ITEM_CARD_CLASSES}"
          href=${this._href}
          target=${this.item.target ?? "_blank"}
          rel="noopener noreferrer"
        >
          ${this._renderCardContent()}
          ${this.item.target === "_blank"
            ? html`<span class="visually-hidden sr-only"
                >(opens in new tab)</span
              >`
            : nothing}
        </a>
      `;
    }

    if (onClick) {
      return html`
        <button class="${ITEM_CARD_CLASSES}" type="button" @click=${onClick}>
          ${this._renderCardContent()}
        </button>
      `;
    }

    // No href and no onClick — render a button that fires support-select so the
    // host (Bridge Bar) can route to the appropriate destination (e.g. chat, auth).
    return html`
      <button
        class="${ITEM_CARD_CLASSES}"
        type="button"
        @click=${this._emitSupportSelect}
      >
        ${this._renderCardContent()}
      </button>
    `;
  }
}

if (!customElements.get("support-menu-item")) {
  customElements.define("support-menu-item", SupportMenuItem);
}
